import { identityLine, needsReauth } from '../../lib/channelMeta';
import { phoneBlocked } from './callOptions';
import type { RcDialer } from './useRcDialer';
import type { TwilioPhone } from './useTwilioPhone';

/**
 * One way to start a call to a number that is not in front of you: a phone
 * this agent can open. Mirrors `CallOption` in shape — what it is, and why it
 * cannot run right now — because the New call control is the same question
 * as a Call button with no number yet.
 */
export interface NewCallRoute {
  key: 'ringcentral' | 'twilio';
  label: string;
  hint: string;
  blocked: string | null;
  open: () => void;
}

/**
 * Which phones this agent could open a keypad on, best first.
 *
 * Existence and readiness are kept apart, the way callOptions does: a phone
 * that is not here at all (no RingCentral line, no Twilio voice channel) is
 * left out entirely, while one that is here but cannot run right now is listed
 * with its reason. So a tenant with one phone system sees one button that
 * opens it, never a menu of two greyed rows.
 */
export function buildNewCallRoutes({
  dialer,
  phone,
}: {
  dialer: RcDialer | null;
  phone: TwilioPhone | null;
}): NewCallRoute[] {
  const routes: NewCallRoute[] = [];
  if (dialer?.available) {
    routes.push({
      key: 'ringcentral',
      label: 'RingCentral dialer',
      hint: 'calls as your RingCentral caller id',
      blocked: dialer.hasCall ? 'Already on a call.' : null,
      open: () => {
        // One panel at a time: the two share a corner.
        phone?.hidePanel();
        dialer.openPanel();
      },
    });
  }
  if (phone?.inbox) {
    routes.push({
      key: 'twilio',
      label: 'Twilio keypad',
      // One browser holds one Twilio number (see useTwilioPhone), so this is
      // a statement, not a choice.
      hint: `calls as ${identityLine(phone.inbox)}`,
      // Same order as the Call button's browser route: a device that says it is
      // ready registered on credentials that have since gone stale, so the pad
      // would open, dial, and be refused by Twilio with nothing on screen.
      blocked: needsReauth(phone.inbox)
        ? `${identityLine(phone.inbox)} needs reconnecting.`
        : phoneBlocked(phone),
      open: () => {
        dialer?.closePanel();
        phone.showPanel();
      },
    });
  }
  return routes;
}

